import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import ExperienceForm from '../components/ExperienceForm';

const ExperiencePage = () => {
  const [experiences, setExperiences] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/experience');
      setExperiences(response.data || []);
    } catch (err) {
      setError('Failed to load experiences');
      setExperiences([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddExperience = () => {
    setEditingExp(null);
    setShowModal(true);
  };

  const handleEditExperience = (exp) => {
    setEditingExp(exp);
    setShowModal(true);
  };

  const handleDeleteExperience = async (id) => {
    if (!window.confirm('Are you sure you want to delete this experience?')) {
      return;
    }

    try {
      await apiClient.delete(`/experience/${id}`);
      setExperiences(experiences.filter(e => e.id !== id));
      showSuccess('Experience deleted successfully');
    } catch (err) {
      setError('Failed to delete experience');
    }
  };

  const handleSaveExperience = async (expData) => {
    try {
      if (editingExp) {
        const response = await apiClient.put(`/experience/${editingExp.id}`, expData);
        setExperiences(experiences.map(e => e.id === editingExp.id ? response.data : e));
        showSuccess('Experience updated successfully');
      } else {
        const response = await apiClient.post(`/experience`, expData);
        setExperiences([...experiences, response.data]);
        showSuccess('Experience created successfully');
      }
      setShowModal(false);
    } catch (err) {
      setError('Failed to save experience');
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <div>
      <div className="content-header">
        <h2>Experience Management</h2>
        <button className="add-btn" onClick={handleAddExperience}>
          + Add New Experience
        </button>
      </div>

      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {isLoading ? (
        <div className="table-container">
          <div className="no-data">Loading experiences...</div>
        </div>
      ) : experiences.length === 0 ? (
        <div className="table-container">
          <div className="no-data">No experiences yet. Add your first experience!</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {experiences.map(exp => (
            <div key={exp.id} className="message-card" style={{ borderLeftColor: '#3498db' }}>
              <h4>{exp.role}</h4>
              <div style={{ color: '#667eea', fontSize: '14px', marginBottom: '10px' }}>
                📍 {exp.company} - {exp.location}
              </div>
              <div style={{ color: '#999', fontSize: '13px', marginBottom: '10px' }}>
                📅 {exp.period}
              </div>
              <p>{Array.isArray(exp.description) ? exp.description.join(' • ') : exp.description}</p>
              <div className="action-buttons" style={{ marginTop: '10px' }}>
                <button className="edit-btn" onClick={() => handleEditExperience(exp)}>
                  Edit
                </button>
                <button className="delete-btn" onClick={() => handleDeleteExperience(exp.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <ExperienceForm
              experience={editingExp}
              onSave={handleSaveExperience}
              onCancel={() => setShowModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperiencePage;
